import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { toast } from 'react-toastify';

import { ThemeProvider } from '@mui/material/styles';

import Langs, { Localize } from '../../app/lang/langs';
import { vlearnInputSx, vlearnTheme, vlearnType } from '../../components/vlearn/vlearnUi';
import {
    getVLearnSettings, listVLearnCategories, closeCourse, setCurrentLesson,
    saveVLearnCourse
} from './vlearnSlice';

import CourseCatalog from './CourseCatalog';
import CoursePlayer from './CoursePlayer';
import CourseEditor from './CourseEditor';
import CourseDashboard from './CourseDashboard';
import QuizPlayer from './QuizPlayer';
import QuizEditor from '../../components/vlearn/quiz/QuizEditor';
import VLearnSettings from './VLearnSettings';

/**
 * V-Learning — the module's entry point, mounted from `Body.js` as `case "vlearn"`.
 *
 * ── Why navigation is local state rather than routes ─────────────────────────────────
 *
 * `screen` is a `useState`, not a URL segment, which matches how V-Sécurité and V-Comp are
 * built: this application's router owns one `view` parameter per module and everything below
 * it is the module's own business. Following that convention keeps V-Learning consistent with
 * its neighbours.
 *
 * The cost is real and worth naming: a course is not deep-linkable, and the browser's back
 * button leaves the module rather than stepping back a screen. `goBack()` exists to give the
 * in-app back affordance the browser's cannot, and every screen is given one. If V-Platform
 * ever adopts nested routes, this component is the single place that changes.
 *
 * ── Why there is no permission check here ────────────────────────────────────────────
 *
 * Deliberately none, for the same reason `Body.js` has none for `vresume` and `vreference`:
 * every collaborator takes the courses assigned to them, so the `vlearn` scope does not grant
 * ENTRY to the module — it only widens which courses a user reaches and whether they may
 * author. That is a per-row decision, made server-side by `VLearnBaseController` and reported
 * back in `state.vlearn.access` and `state.vlearn.reach`, which is what the screens below draw
 * their controls from.
 *
 * A `checkPermission` gate here would lock every employee out of their own mandatory training.
 */
export default function VLearnView() {

    const dispatch = useDispatch()

    const reach = useSelector((state) => state.vlearn.reach)
    // `state.settings.isMobile`, not `state.common.isMobile` — commonSlice has no such key,
    // so the latter was always undefined and the module applied desktop padding on a phone.
    // The other 85 readers in this codebase all use settings.
    const isMobile = useSelector((state) => state.settings.isMobile)

    /**
     * Which screen is showing, and what it is showing it for.
     *
     * Held as one object rather than four separate `useState`s so a transition is a single
     * render: setting a screen and its course id separately means one render where the player
     * is mounted with the previous course's id, which fires a fetch nobody wanted.
     */
    const [screen, setScreen] = useState({
        name: 'catalog',
        idCourse: null,
        idQuiz: null,
        idLesson: null,
        /** Where a Back press should land. The quiz is reached from the player, not the catalogue. */
        returnTo: 'catalog'
    })

    // React 18's StrictMode mounts every component twice in development. Without this the
    // module fires its two bootstrap requests twice on every open — the same guard
    // VSecuriteView uses.
    const bootstrapped = useRef(false)

    useEffect(() => {
        if (bootstrapped.current) return
        bootstrapped.current = true
        // Both are module-wide and cheap, and both are needed before any screen can render
        // correctly: `settings` decides which lesson types exist and carries the caller's
        // reach, and the categories populate every filter and picker.
        dispatch(getVLearnSettings({}))
        dispatch(listVLearnCategories({}))
    }, [dispatch])

    /**
     * Opens the course player.
     *
     * `from` is where the click came from, and it decides which hat the player wears:
     *
     *   'learning'  the catalogue's My learning tab — the LEARNER journey. The player shows
     *               no Dashboard and no Edit, even for someone who authors this course, for
     *               the same reason the card does not offer Start next to Edit: the two tabs
     *               are two jobs, and mixing them puts author controls in front of someone
     *               who opened the course to take it.
     *   'managing'  the Courses I manage tab, or the dashboard's "Open course".
     *   'editor'    the editor's Preview button. Also sets `returnTo` so Back returns to the
     *               tab they were authoring rather than dropping them on the catalogue.
     *
     * The author actions still require the access flags as well — this only decides whether
     * to offer them at all.
     */
    const openCourse = useCallback((idCourse, from = 'managing') => {
        // Opening a course from a tab is the clearest possible statement of which tab the
        // user is working in, so the return tab follows it. 'editor' is not a tab, so a
        // preview leaves the memory alone.
        if (from === 'learning' || from === 'managing') setCatalogScope(from)
        setScreen({
            name: 'player',
            idCourse,
            idQuiz: null,
            idLesson: null,
            returnTo: from === 'editor' ? 'editor' : 'catalog',
            learnerMode: from === 'learning'
        })
    }, [])

    /**
     * Which catalogue tab to return to.
     *
     * Held here because CourseCatalog unmounts on every screen change and cannot remember on
     * its own.
     *
     * It tracks THE TAB THE USER WAS LAST ON — reported by the catalogue when they switch,
     * and by `openCourse` when they open something from a tab. An earlier version only ever
     * set it to 'managing', when the user went off to author: sticky, never set back, so
     * once anyone had edited or created a course every later return landed on "Courses I
     * manage", including after opening a course from My learning.
     */
    const [catalogScope, setCatalogScope] = useState('learning')

    const editCourse = useCallback((idCourse) => {
        setCatalogScope('managing')
        setScreen({ name: 'editor', idCourse, idQuiz: null, idLesson: null, returnTo: 'catalog' })
    }, [])

    const openDashboard = useCallback((idCourse, returnTo = 'catalog') => {
        setScreen({ name: 'dashboard', idCourse, idQuiz: null, idLesson: null, returnTo })
    }, [])

    /**
     * Opens a quiz lesson FOR A LEARNER — the take-it screen.
     *
     * `returnTo` is carried explicitly so Back lands on the course the learner came from
     * rather than the catalogue — losing your place in a twelve-lesson course because you
     * finished a quiz would be its own small betrayal.
     */
    const openQuiz = useCallback((idQuiz, idLesson, idCourse) => {
        setScreen({ name: 'quiz', idCourse, idQuiz, idLesson, returnTo: 'player' })
    }, [])

    /**
     * Opens a quiz FOR AN AUTHOR — the settings-and-questions editor.
     *
     * A separate screen from `openQuiz`, and it has to be. Both were wired to the same
     * `'quiz'` case, so pressing Edit on a quiz in the course editor opened `QuizPlayer` —
     * the learner's view. That is why the quiz could not be named and offered no options:
     * `QuizEditor`, which holds the title field, the pass mark, the attempt rules and the
     * question list, was never rendered anywhere in the application.
     *
     * `returnTo: 'editor'` because an author reached this from the course editor's Quizzes
     * tab and expects Back to return there, not to the catalogue.
     */
    const openQuizEditor = useCallback((idQuiz, idCourse) => {
        setCatalogScope('managing')
        // `returnTab` so closing the quiz editor lands back on the Quizzes tab rather than
        // on Details, which is where the remounted editor's own default would put it.
        setScreen({
            name: 'quizEditor', idCourse, idQuiz, idLesson: null,
            returnTo: 'editor', returnTab: 'quizzes'
        })
    }, [])

    const openSettings = useCallback(() => {
        setScreen({ name: 'settings', idCourse: null, idQuiz: null, idLesson: null, returnTo: 'catalog' })
    }, [])

    /**
     * Creating a course.
     *
     * A course cannot be edited before it exists: `CourseEditor` is addressed by id and every
     * one of its tabs — audience, instructors, quizzes — needs one to talk to the server. So
     * "Create a course" cannot simply open the editor on nothing; it has to create the row
     * first and then open the editor on the id that comes back.
     *
     * The dialog asks for the title alone because that is the one field `saveCourse` requires;
     * everything else has a server-side default and is edited in the Details tab straight
     * after. Asking for it up front rather than creating "Untitled" immediately is what keeps
     * an abandoned click from leaving a junk draft in the catalogue — and the drafts here are
     * visible to every co-manager, so junk is not private.
     */
    const [newCourseOpen, setNewCourseOpen] = useState(false)
    const [newCourseTitle, setNewCourseTitle] = useState('')
    const [creating, setCreating] = useState(false)
    const [newCourseError, setNewCourseError] = useState(false)

    const openCreateCourse = useCallback(() => {
        setNewCourseTitle('')
        setNewCourseError(false)
        setNewCourseOpen(true)
    }, [])

    const submitNewCourse = useCallback(async () => {
        const title = newCourseTitle.trim()
        if (!title) {
            setNewCourseError(true)
            return
        }
        // A ref would be overkill for a dialog that unmounts on success; the disabled button
        // plus this flag is enough to stop a double submit creating two courses.
        if (creating) return
        setCreating(true)

        const response = await dispatch(saveVLearnCourse({ title }))
        setCreating(false)

        const data = response && response.payload && response.payload.responseData
        if (!data || data.status !== 1 || !data.id) {
            // The server's own message where there is one — `saveCourse` answers 403 for a
            // caller with no `vlearn:edit` anywhere, and that is worth saying plainly rather
            // than replacing with a generic failure.
            const error = response && response.payload && response.payload.responseError
            const message = (error && error.data && error.data.error)
                || Localize('VLEARN_SAVE_FAILED')
            toast.error(message)
            return
        }

        setNewCourseOpen(false)
        setCatalogScope('managing')
        // Straight into the editor on the real id. The editor fetches the course itself, so
        // nothing needs to be seeded here.
        setScreen({
            name: 'editor', idCourse: data.id,
            idQuiz: null, idLesson: null, returnTo: 'catalog'
        })
    }, [dispatch, newCourseTitle, creating])

    /**
     * The in-app back affordance.
     *
     * Clears the course from the store on the way out of a course-scoped screen. Without
     * that, the next course renders the previous one's curriculum for one frame before its
     * own response lands — and a stale `access` object for that frame is a stale set of
     * buttons.
     */
    /*
     * The dispatches happen OUTSIDE the state updater, deliberately.
     *
     * A `setState(current => ...)` updater must be pure. React 18's StrictMode
     * double-invokes it, so a dispatch inside fired twice, and it landed during the render
     * phase — notifying every other subscriber to this slice mid-render. Both actions happen
     * to be idempotent today, which is the only reason it was not visibly broken; that is one
     * non-idempotent action away from a real defect.
     *
     * Reading `screen` from the closure is safe here because `goBack` is only ever called
     * from an event handler, never batched against another change to the same state.
     */
    const goBack = useCallback(() => {
        const target = screen.returnTo || 'catalog'
        if (target === 'catalog') {
            dispatch(closeCourse())
            setScreen({
                name: 'catalog', idCourse: null, idQuiz: null, idLesson: null,
                returnTo: 'catalog'
            })
            return
        }
        // Back from a quiz to the course it belongs to. The player's unmount already ran
        // `closeCourse`, so the target screen refetches — this only restores the selection.
        dispatch(setCurrentLesson(screen.idLesson || null))
        setScreen({ ...screen, name: target, idQuiz: null, returnTo: 'catalog' })
    }, [dispatch, screen])

    let body = null
    switch (screen.name) {
        case 'player':
            body = (
                <CoursePlayer
                    idCourse={screen.idCourse}
                    // True when the course was opened from "My learning": the player then
                    // hides the author actions regardless of this user's rights.
                    learnerMode={!!screen.learnerMode}
                    onBack={goBack}
                    onOpenDashboard={() => openDashboard(screen.idCourse, 'player')}
                    onEditCourse={() => editCourse(screen.idCourse)}
                    onOpenQuiz={(idQuiz, idLesson) => openQuiz(idQuiz, idLesson, screen.idCourse)}
                />
            )
            break
        case 'editor':
            body = (
                <CourseEditor
                    idCourse={screen.idCourse}
                    initialTab={screen.returnTab}
                    onBack={goBack}
                    onOpenDashboard={() => openDashboard(screen.idCourse, 'editor')}
                    onPreview={() => openCourse(screen.idCourse, 'editor')}
                    // The authoring screen, not QuizPlayer — see openQuizEditor.
                    onEditQuiz={(idQuiz) => openQuizEditor(idQuiz, screen.idCourse)}
                />
            )
            break
        case 'dashboard':
            body = (
                <CourseDashboard
                    idCourse={screen.idCourse}
                    onBack={goBack}
                    onOpenCourse={() => openCourse(screen.idCourse, 'managing')}
                />
            )
            break
        case 'quizEditor':
            body = (
                <QuizEditor
                    idCourse={screen.idCourse}
                    idQuiz={screen.idQuiz}
                    onBack={goBack}
                />
            )
            break
        case 'quiz':
            body = (
                <QuizPlayer
                    idCourse={screen.idCourse}
                    idQuiz={screen.idQuiz}
                    idLesson={screen.idLesson}
                    onExit={goBack}
                />
            )
            break
        case 'settings':
            body = <VLearnSettings onBack={goBack} />
            break
        case 'catalog':
        default:
            body = (
                <CourseCatalog
                    onOpenCourse={openCourse}
                    onEditCourse={editCourse}
                    // NOT `editCourse`: that expects an existing id, and passing none opened
                    // the editor on `undefined`, which fetched nothing and rendered a blank
                    // page. Creating a course is its own step — see openCreateCourse.
                    onCreateCourse={openCreateCourse}
                    initialScope={catalogScope}
                    onScopeChange={setCatalogScope}
                    // The module's only entry point to the admin settings, since this view
                    // no longer renders a header of its own.
                    onOpenSettings={reach && reach.isAdmin ? openSettings : null}
                />
            )
            break
    }

    return (
        /*
         * Everything in V-Learning renders inside this ThemeProvider.
         *
         * There is no global MUI theme in this application, so without it every control in
         * the module inherits MUI's default blue `primary` — focused inputs, switches,
         * radios, sliders, DataGrid checkboxes. See `vlearnTheme` in vlearnUi.js for why
         * this is one provider at the root rather than an `sx` prop per control.
         *
         * It wraps the scroll container rather than sitting inside it so that Dialogs and
         * Select menus, which render through a portal outside this DOM subtree, are still
         * inside the React context and pick the theme up.
         */
        <ThemeProvider theme={vlearnTheme}>
        {/*
         * THIS BOX IS THE MODULE'S SCROLL CONTAINER, and it has to be.
         *
         * `Common.module.css` gives `.body` — the div `Body.js` renders every view inside —
         * `position: fixed; height: 100%` with no overflow rule. A fixed ancestor of that
         * height cannot be scrolled by the page, so anything taller than the viewport is
         * simply unreachable unless the module provides its own scroll region. A course with
         * twelve lessons is comfortably taller than the viewport.
         *
         * `height: 100%` + `overflowY: auto` is what `VSecuriteView`'s `containerSx` does and
         * what `VResume.module.css` does on its own root class. `-webkit-overflow-scrolling`
         * is there for momentum scrolling on iOS, matching VSecuriteView.
         */}
        <Box sx={{
            height: '100%',
            overflowY: 'auto',
            WebkitOverflowScrolling: 'touch',
            /*
             * NO TOP PADDING, and that is load-bearing.
             *
             * `position: sticky` measures its offsets from the scroll container's PADDING
             * box, so with a 20px padding-top a header at `top: 0` pinned 20px below the
             * visual top — and the padding band is inside the scrollable region, so form
             * fields scrolled up through it and appeared ABOVE the header. That is the
             * artifact CourseEditor showed.
             *
             * With no top padding the padding box and the border box share an edge, so
             * `top: 0` means the top. Each screen supplies its own top spacing instead, which
             * also lets the editor's sticky header own the space it needs to cover.
             */
            /*
             * The BOTTOM padding on a phone is not cosmetic.
             *
             * MobileMenu replaces the rail entirely below 768px and puts its opener at
             * `position: fixed; bottom: 24px; right: 24px` at 56px square — so the bottom
             * ~80px of every screen in the application is covered by a floating button. With
             * only 12px here the last card in the catalogue, the "Load more" button and the
             * player's "Next lesson" all sat underneath it and could not be tapped, with no
             * way to scroll them clear because they were already at the end of the scroll.
             *
             * `env(safe-area-inset-bottom)` is added on top for the home-indicator strip on a
             * notched phone, which is inset from the same edge.
             */
            padding: (isMobile === 2 || isMobile === 3)
                ? '0 12px calc(96px + env(safe-area-inset-bottom, 0px))'
                : '0 26px 26px',
            // The inner column is centred and capped, but the SCROLLING element stays
            // full-width — capping the scroller instead would leave dead, unclickable
            // gutters either side of the scrollbar on a wide screen.
            '& > *': { maxWidth: '1600px', marginInline: 'auto', width: '100%' }
        }}>
            {/*
              * There is deliberately NO header here.
              *
              * Every screen below renders its own — CoursePlayer, CourseEditor,
              * CourseDashboard, QuizPlayer, VLearnSettings and CourseCatalog each own a
              * title, a back affordance where they need one, and their own actions. A second
              * header at this level duplicated all of it: the course title appeared twice,
              * once here and once in the editor's sticky header, with two back buttons.
              *
              * The one thing this header uniquely provided, the admin Settings entry, now
              * lives in CourseCatalog's header — the module's root screen, and the only one
              * that had nowhere else to put it.
              */}
            {body}

            <Dialog
                open={newCourseOpen}
                onClose={() => (creating ? null : setNewCourseOpen(false))}
                fullWidth
                maxWidth="xs"
                PaperProps={{ sx: { borderRadius: '14px' } }}
            >
                <DialogTitle sx={{ ...vlearnType.sectionTitle, pb: 0.5 }}>
                    <Langs str='VLEARN_NEW_COURSE_TITLE' />
                </DialogTitle>
                <DialogContent>
                    <Typography sx={{ ...vlearnType.body, mb: 2 }}>
                        <Langs str='VLEARN_NEW_COURSE_HINT' />
                    </Typography>
                    <TextField
                        autoFocus
                        fullWidth
                        size="small"
                        // 16px below `sm` or iOS Safari zooms the page on focus and never
                        // zooms back — see `vlearnInputSx`.
                        sx={vlearnInputSx}
                        value={newCourseTitle}
                        // 190 to match the column, so a long title is trimmed here rather
                        // than becoming a strict-mode error on the way in.
                        inputProps={{ maxLength: 190 }}
                        onChange={(event) => {
                            setNewCourseTitle(event.target.value)
                            if (newCourseError) setNewCourseError(false)
                        }}
                        // Enter submits: this dialog has one field, and reaching for the
                        // mouse to confirm a single line of text is friction with no purpose.
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') {
                                event.preventDefault()
                                submitNewCourse()
                            }
                        }}
                        label={<Langs str='VLEARN_FIELD_TITLE' />}
                        error={newCourseError}
                        helperText={newCourseError
                            ? <Langs str='VLEARN_FIELD_TITLE_REQUIRED' />
                            : ' '}
                    />
                </DialogContent>
                {/* `gap` rather than MUI's default 8px between the two: a cancel that
                    discards a typed title should not share an edge with the confirm on a
                    surface operated by thumb. `minHeight: 44` is the tap-target floor. */}
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button
                        onClick={() => setNewCourseOpen(false)}
                        disabled={creating}
                        sx={{ textTransform: 'none', minHeight: 44, color: 'var(--text-secondary, #4b5263)' }}
                    >
                        <Langs str='VLEARN_CANCEL' />
                    </Button>
                    <Button
                        onClick={submitNewCourse}
                        variant="contained"
                        disabled={creating || !newCourseTitle.trim()}
                        sx={{
                            textTransform: 'none', boxShadow: 'none', minHeight: 44,
                            backgroundColor: 'var(--orange, rgb(255,144,21))',
                            '&:hover': { backgroundColor: '#e07f0d', boxShadow: 'none' }
                        }}
                    >
                        <Langs str='VLEARN_CREATE_COURSE' />
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
        </ThemeProvider>
    )
}
